/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Build an email", description = "Write an email in the drag-and-drop or HTML editor, start from a template, and reuse content blocks and images.", lastUpdatedDate = new Date(1792368000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Build an email",
        description: "Write an email in the drag-and-drop or HTML editor, start from a template, and reuse content blocks and images.",
        lastUpdatedDate: "Mon Oct 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Create a message",
          "depth": 0,
          "id": "create-a-message"
        }, {
          "value": "Start from a template",
          "depth": 0,
          "id": "start-from-a-template"
        }, {
          "value": "Reuse content across emails",
          "depth": 0,
          "id": "reuse-content-across-emails"
        }, {
          "value": "Add images",
          "depth": 0,
          "id": "add-images"
        }, {
          "value": "Save and publish",
          "depth": 0,
          "id": "save-and-publish"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      h2: "h2",
      ol: "ol",
      li: "li",
      a: "a",
      ul: "ul"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, lifecycle and CRM teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: "An email channel and at least one sender identity, set up with your Hightouch team"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Email content lives in a message. Write the subject and preheader, build the body in the drag-and-drop or HTML editor, and save it."
        })
      }), "\n", _jsx(_components.h2, {
        id: "create-a-message",
        children: "Create a message"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Native Delivery → Messages"
          }), " and add a message for the ", _jsx(_components.strong, {
            children: "Email"
          }), " channel."]
        }), "\n", _jsxs(_components.li, {
          children: ["Choose an editor: ", _jsx(_components.strong, {
            children: "Drag and drop"
          }), " builds the email visually. ", _jsx(_components.strong, {
            children: "HTML"
          }), " takes raw markup."]
        }), "\n", _jsx(_components.li, {
          children: "Enter a subject, and a preheader if you want one. The preheader is the preview text most mail clients show next to the subject."
        }), "\n", _jsxs(_components.li, {
          children: ["Build the body, then select ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Choosing ", _jsx(_components.strong, {
            children: "HTML"
          }), " is permanent. You can switch a drag-and-drop email to the HTML editor later, but not back. Start with drag-and-drop unless you intend to hand-write the markup."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "start-from-a-template",
        children: "Start from a template"
      }), "\n", _jsxs(_components.p, {
        children: ["Templates are reusable email structures. To start from one, open the ", _jsx(_components.strong, {
          children: "⋯"
        }), " menu and select ", _jsx(_components.strong, {
          children: "Browse templates…"
        }), ", then ", _jsx(_components.strong, {
          children: "Apply"
        }), ". Templates are filtered to your current editor. An HTML message only offers HTML templates."]
      }), "\n", _jsx(_components.p, {
        children: "Starting from a template copies its content into your message. Edits you make afterward affect this message only, and leave the template as it was."
      }), "\n", _jsxs(_components.p, {
        children: ["To save the email you're working on for reuse, select ", _jsx(_components.strong, {
          children: "Save as template"
        }), " from the same menu. Manage the library in ", _jsx(_components.strong, {
          children: "Assets → Templates"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["To bring in existing markup, select ", _jsx(_components.strong, {
          children: "Upload HTML file"
        }), " from the ", _jsx(_components.strong, {
          children: "⋯"
        }), " menu. This is available in the HTML editor."]
      }), "\n", _jsx(_components.h2, {
        id: "reuse-content-across-emails",
        children: "Reuse content across emails"
      }), "\n", _jsxs(_components.p, {
        children: ["Content blocks are shared fragments, such as a header or a promotional module, that you maintain once and reference from many emails. In the HTML editor, select ", _jsx(_components.strong, {
          children: "Insert content block"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Editing a block updates every email that references it, including messages that are already live. Manage blocks in ", _jsx(_components.strong, {
          children: "Assets → Content blocks"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["To change a block in one email without affecting the others, select the block and choose ", _jsx(_components.strong, {
          children: "Detach"
        }), ". The content stays where it is and becomes part of that message. You can edit it freely, and it stops receiving updates to the original block."]
      }), "\n", _jsxs(_components.p, {
        children: ["Your email footer is configured once for the workspace under ", _jsx(_components.strong, {
          children: "Settings → Brand guidelines"
        }), ", and it carries the unsubscribe link. Hightouch warns you if the footer is missing one."]
      }), "\n", _jsx(_components.h2, {
        id: "add-images",
        children: "Add images"
      }), "\n", _jsxs(_components.p, {
        children: ["Images live in a shared ", _jsx(_components.a, {
          href: "/lifecycle-studio/images",
          children: "image library"
        }), " you can reference from any email. Upload them directly, or connect your existing digital asset manager and import from it."]
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/context-hub/connections#digital-asset-managers",
          children: "digital asset managers"
        }), " for the providers Hightouch connects to and how to connect one. If you use a different one, ask your Hightouch contact."]
      }), "\n", _jsx(_components.p, {
        children: "A library image is available to every email. Update it in one place and it changes wherever it's referenced."
      }), "\n", _jsx(_components.h2, {
        id: "save-and-publish",
        children: "Save and publish"
      }), "\n", _jsxs(_components.p, {
        children: ["What ", _jsx(_components.strong, {
          children: "Save"
        }), " does depends on the state of what you're editing:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "State"
            }), _jsx(_components.th, {
              children: "What Save does"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Draft campaign, or a message not yet live"
            }), _jsx(_components.td, {
              children: "Publishes immediately."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Campaign past Draft"
            }), _jsx(_components.td, {
              children: "Stages your edits as a draft against the live version."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["When edits are staged, the message shows an ", _jsx(_components.strong, {
          children: "Unpublished changes"
        }), " badge, and recipients keep receiving the live version until you select ", _jsx(_components.strong, {
          children: "Publish changes"
        }), ". This lets you revise an active campaign without interrupting it."]
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/native-delivery/personalization",
            children: "Personalize a message"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/native-delivery/preview-and-test",
            children: "Preview and test"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
